// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type {
  CustomPropertyTypeName,
  CustomProperty,
  CustomPropertyDataTypeMap,
  CreateCustomPropertyBody,
  StrongOmit,
} from '@evolvemep/api-ts';
import { Button, TextInput, Stack, Flex, Text, Divider, ActionIcon, type MantineSize } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { allDataTypes, dataTypeIconMap, dataTypeLabelMap } from 'components/Mantine/CustomProperties/constants';
import { useCustomProperties } from 'components/Mantine/CustomProperties/useCustomProperties';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { TextInputWrapped, TextareaWrapped } from 'components/Mantine/TextInputWrapped';
import type { WrappedSelectProps } from 'components/Mantine/TypeSafeSelect/types';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import type { WithRequiredNotNull, WithNullable } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useIsMobile } from 'hooks/useIsMobile';

type Props = {
  customPropertyTypeName: CustomPropertyTypeName;
  buttonLabel?: string;
  buttonSize?: MantineSize;
  withButtonIcon?: boolean;
} & (
  | {
      customProperty: CustomProperty;
      opened: boolean;
      onClose: () => void;
    }
  | {
      customProperty?: never;
      opened?: boolean;
      onClose?: () => void;
    }
);

const ItemRenderer: WrappedSelectProps<keyof CustomPropertyDataTypeMap, object>['renderOption'] = ({
  option: { value: dataType, label },
}) => (
  <Flex align="center" gap="xs">
    <EvolveIcon {...dataTypeIconMap[dataType]} />
    <Text>{label}</Text>
  </Flex>
);

const maxAllowedDropdownValues = 20;

const CustomPropertyFields = ({
  customPropertyTypeName,
  customProperty,
}: Pick<Props, 'customPropertyTypeName' | 'customProperty'>) => {
  const { addProperty, updateProperty } = useCustomProperties(customPropertyTypeName);
  const isMobile = useIsMobile();

  const form = useForm<StrongOmit<WithNullable<CreateCustomPropertyBody, 'dataType'>, 'customPropertyTypeId'>>({
    initialValues: customProperty ?? {
      dataType: null,
      name: '',
      description: '',
      options: [],
    },
    validate: {
      dataType: (v) => !v,
      name: (v) => !v,
      options: (v, values) => values.dataType === 'dropdown' && !v?.some((o) => !!o.name),
    },
  });

  const onSubmit = async ({ options, ...values }: typeof form.values) => {
    const body = {
      ...(values as WithRequiredNotNull<typeof values>),
      options: values.dataType === 'dropdown' ? options?.filter((o) => !!o.name.trim()) : undefined,
    };
    await (isNil(customProperty) ? addProperty(body) : updateProperty(customProperty.customPropertyId, body)).then(
      (updatedProperty) => {
        const verb = isNil(customProperty) ? 'created' : 'updated';
        notifications.show({
          title: `Successfully ${verb} custom property`,
          message: `Custom property ${updatedProperty.name} ${verb}.`,
          color: 'green',
        });
      },
    );
  };

  useFormPopover(form, onSubmit);

  const maxValuesReached = (form.values.options?.length ?? 0) >= maxAllowedDropdownValues;

  return (
    <Stack gap="xs">
      <WrappedSelect
        label="Property Type"
        placeholder="Select a property type..."
        data={allDataTypes.map((dt) => ({
          value: dt,
          label: dataTypeLabelMap[dt],
        }))}
        disabled={isNotNil(customProperty)}
        required
        renderOption={ItemRenderer}
        leftSection={
          form.values.dataType ? <EvolveIcon {...dataTypeIconMap[form.values.dataType]} size="sm" /> : undefined
        }
        {...form.getInputProps('dataType')}
      />
      <TextInput label="Name" maxLength={64} required {...form.getInputProps('name')} />
      <TextareaWrapped label="Tooltip Description" maxLength={255} {...form.getInputProps('description')} />
      {form.values.dataType === 'dropdown' && (
        <>
          <Flex align="center" gap="xs">
            <Divider label="Options" flex={1} />
            <AgGridStyleTooltip
              label={`Max values reached (${maxAllowedDropdownValues})`}
              hidden={!maxValuesReached}
              withArrow
              openDelay={0}
            >
              <Button
                variant="light"
                size="compact-sm"
                disabled={maxValuesReached}
                onClick={() =>
                  form.insertListItem('options', {
                    name: '',
                  })
                }
              >
                Add option
              </Button>
            </AgGridStyleTooltip>
          </Flex>
          <Stack
            /* On desktop, cap the options list so a long list doesn't make the popover
                unwieldily tall; the list scrolls internally. On mobile the popover body already
                scrolls as a single region (see WrappedPopover), so an inner cap here would
                just create a second, competing scroll area. */
            mah={isMobile ? undefined : 250}
            style={{
              overflowY: isMobile ? undefined : 'auto',
            }}
            gap="xs"
          >
            {form.values.options?.map((o, i) => (
              <Flex key={o.customPropertyOptionId ?? i} gap="xs" align="center">
                <TextInputWrapped
                  size="xs"
                  flex={1}
                  autoFocus
                  data-autofocus
                  maxLength={64}
                  onSubmit={() => !maxValuesReached && form.insertListItem('options', { name: '' }, i + 1)}
                  {...form.getInputProps(`options.${i}.name`)}
                />
                <AgGridStyleTooltip label="Remove option">
                  <ActionIcon onClick={() => form.removeListItem('options', i)} variant="transparent" size="sm">
                    <EvolveIcon icon="Delete" light size="sm" />
                  </ActionIcon>
                </AgGridStyleTooltip>
              </Flex>
            ))}
          </Stack>
          {form.values.options?.length === 0 && (
            <Text c="dimmed" fz="xs">
              Add at least one option.
            </Text>
          )}
        </>
      )}
    </Stack>
  );
};

export const AddEditCustomPropertyPopover = ({
  customPropertyTypeName,
  buttonLabel = 'Add property',
  withButtonIcon = true,
  buttonSize,
  ...props
}: Props) => {
  const [opened, setOpened] = useState(false);

  return (
    <FormPopover
      opened={props.opened || opened}
      onClose={props.onClose ?? (() => setOpened(false))}
      target={
        isNil(props.customProperty) ? (
          <Button
            variant="light"
            size={buttonSize}
            leftSection={withButtonIcon ? <EvolveIcon icon="Add" size="sm" /> : undefined}
            onClick={() => setOpened(true)}
          >
            {buttonLabel}
          </Button>
        ) : undefined
      }
      middlewares={{
        shift: {
          crossAxis: true,
        },
      }}
    >
      <CustomPropertyFields customPropertyTypeName={customPropertyTypeName} customProperty={props.customProperty} />
    </FormPopover>
  );
};
